import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, CheckCircle2, AlertCircle, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrackOrder = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
    const interval = setInterval(fetchOrder, 5000);
    return () => clearInterval(interval);
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const { data } = await axios.get(`${API}/orders/${orderId}`);
      setOrder(data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Comanda nu a fost găsită');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8E4DD] flex items-center justify-center">
        <p className="text-[#999]">Se încarcă...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#E8E4DD] flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-[#E54B4B] mx-auto mb-4" />
          <p className="text-[#999] mb-4">Comanda nu a fost găsită</p>
          <Button onClick={() => navigate('/')} className="accent-button">
            Înapoi la Meniu
          </Button>
        </div>
      </div>
    );
  }

  const isCompleted = order.status === 'Finalizată';

  return (
    <div className="min-h-screen bg-[#E8E4DD]">
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="text-center mb-6">
          <CheckCircle2 className="w-14 h-14 text-[#5C9F5F] mx-auto mb-3" />
          <h1 className="text-2xl font-semibold text-[#1F1F1F] mb-2">
            Status Comandă
          </h1>
          <p className="text-[#666]">ID: #{order.id.slice(-8).toUpperCase()}</p>
        </div>

        {/* Pickup time + email reminder banner — shown while order is still being prepared */}
        {!isCompleted && (
          <div
            data-testid="pickup-email-notification-banner"
            className="bg-[#FFFBEB] border border-[#FDE68A] rounded-xl p-4 mb-5"
          >
            <p className="text-sm text-[#92400E] leading-relaxed">
              <span className="font-semibold">Notificare importantă:</span>{' '}
              Intervalul orar selectat este estimativ. Te rugăm să urmărești e-mailul — vei primi o notificare pe e-mail când comanda ta este gata de ridicare!
            </p>
          </div>
        )}

        <div className="minimalist-card p-6 mb-5">
          <div className="flex items-center gap-4 mb-4">
            {isCompleted ? (
              <CheckCircle2 className="w-12 h-12 text-[#5C9F5F]" />
            ) : (
              <Clock className="w-12 h-12 text-[#7BA05B]" />
            )}
            <div>
              <h2 className="text-xl font-semibold text-[#1F1F1F]">
                {isCompleted ? 'Comanda este gata!' : 'În pregătire'}
              </h2>
              <p className="text-[#666]">{order.status}</p>
            </div>
          </div>
          
          {isCompleted && (
            <div className="bg-[#F0F8F0] border border-[#5C9F5F] rounded-lg p-4 mt-4">
              <p className="text-[#5C9F5F] font-medium">
                Comanda ta este gata de ridicat! Te așteptăm la cantină.
              </p>
            </div>
          )}

          {order.delay_minutes > 0 && !isCompleted && (
            <div className="bg-[#FFF7E6] border-2 border-[#F5A524] rounded-lg p-4 mt-4" data-testid="delay-banner">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#F5A524] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[#8B5A00]">Întârziere anunțată</p>
                  <p className="text-sm text-[#8B5A00] mt-1">
                    Comanda ta va fi gata cu aproximativ <strong>{order.delay_minutes} minute</strong> mai târziu decât intervalul ales. Îți mulțumim pentru înțelegere.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="minimalist-card p-5 mb-5">
          <h3 className="text-base font-semibold text-[#1F1F1F] mb-4">Detalii Comandă</h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-[#F0F0F0]">
              <span className="text-[#666]">Nume</span>
              <span className="font-medium text-[#1F1F1F]">{order.customer_name}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-[#F0F0F0]">
              <span className="text-[#666]">Telefon</span>
              <span className="font-medium text-[#1F1F1F]">{order.customer_phone}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-[#F0F0F0]">
              <span className="text-[#666]">Ora ridicare</span>
              <span className="font-medium text-[#1F1F1F]">{order.pickup_time}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-[#666]">Total</span>
              <span className="font-semibold text-[#7BA05B] text-xl">{order.total_amount.toFixed(2)} RON</span>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-[#E8E8E8]">
            <h4 className="font-medium text-[#1F1F1F] mb-3">Produse</h4>
            {order.items.map((item, index) => (
              <div key={index} className="flex justify-between py-2">
                <span className="text-[#666]">{item.quantity}x {item.name}</span>
                <span className="font-medium text-[#1F1F1F]">{(item.price * item.quantity).toFixed(2)} RON</span>
              </div>
            ))}
          </div>
        </div>

        <Button
          data-testid="back-home-button"
          onClick={() => navigate('/')}
          variant="outline"
          className="w-full rounded-lg border-[#E8E8E8] hover:bg-[#F8F8F8] py-3 font-medium"
        >
          <Home className="w-5 h-5 mr-2" />
          Înapoi la Meniu
        </Button>
      </main>
    </div>
  );
};

export default TrackOrder;